import React from "react";
import { useForm } from "react-hook-form";
import {
    StackProps,
    ConfirmDialog,
    Form,
    Stack,
    FormDatePicker,
    FormTextField,
    FormActions,
    FormDropdown,
    DropdownOption,
    depascalize,
    mapToDropdownOption
} from "sprint-common-web";

import { CreateDeliveryRequest } from "../../common/api/delivery-api/CreateDeliveryRequest";
import { DeliveryType } from "../../common/types/DeliveryType";
import { useDeliveryInfoForm } from "./useDeliveryInfoForm";

export type DefaultDelivery = {
    file: {
        arrivalDate: string;
        path: string;
        size?: number;
    };
    sender: {
        name?: string;
        email?: string;
        subject?: string;
        message?: string;
    };
    originator?: {
        id: string;
        name: string;
    };
};

const columnStackProps: StackProps = {
    styles: { root: { width: "100%" } },
    tokens: { childrenGap: 20 }
};

export interface DeliveryInfoFormInputs {
    deliveryName: string;
    deliveryEmail: string;
    deliverySubject: string;
    deliveryRef: string;
    deliveryMessage: string;
    deliveryType: string;
    arrivalDate: Date;
    originatorId: string;
}

export interface DeliveryInfoFormProps {
    horizontal?: boolean;
    defaultDelivery?: DefaultDelivery;
    onSubmit(inputs: DeliveryInfoFormInputs): Promise<void>;
    onCancel(): void;
}

export const DeliveryInfoForm: React.VFC<DeliveryInfoFormProps> = (props: DeliveryInfoFormProps) => {
    const {
        defaultValues,
        isCancelDialogHidden,
        originators,
        showCancelDialog,
        hideCancelDialog
    } = useDeliveryInfoForm(props.defaultDelivery);

    const { errors, control, formState, handleSubmit } = useForm<DeliveryInfoFormInputs>({
        defaultValues: defaultValues
    });

    const handleFormSubmit = async (inputs: DeliveryInfoFormInputs): Promise<void> => {
        await props.onSubmit(inputs);
    };

    const handleCancel = () => {
        if (formState.isDirty) {
            showCancelDialog();
        } else {
            props.onCancel();
        }
    };

    const handleCancelDialogConfirm = () => {
        props.onCancel();
    };

    return (
        <>
            <ConfirmDialog
                confirm={{ onClick: handleCancelDialogConfirm }}
                cancel={{ onClick: hideCancelDialog }}
                hidden={isCancelDialogHidden}
                title="Discard changes"
                text="Are you sure to discard current form changes?"
            />

            <Form onSubmit={handleSubmit(handleFormSubmit)} testId="upload-form">
                <Stack horizontal={props.horizontal} styles={{ root: { width: "100%" } }} tokens={{ childrenGap: 20 }}>
                    <Stack {...columnStackProps}>
                        <FormDatePicker
                            label="Arrival date"
                            control={control}
                            name="arrivalDate"
                            errors={errors}
                            rules={{
                                required: "Select arrival date"
                            }}
                        />
                        <FormDropdown
                            options={getDeliveryTypes()}
                            label="Delivery type"
                            control={control}
                            name="deliveryType"
                            disabled={true}
                            errors={errors}
                            rules={{
                                required: "Select delivery type from dropdown"
                            }}
                        />

                        <FormDropdown
                            options={originators.map((originator) => mapToDropdownOption(originator))}
                            control={control}
                            errors={errors}
                            name="originatorId"
                            label="Originator"
                            rules={{
                                required: "Select originator"
                            }}
                        />
                        <FormTextField label="Reference" control={control} name="deliveryRef" errors={errors} />
                    </Stack>
                    <Stack {...columnStackProps}>
                        <FormTextField label="Name" control={control} name="deliveryName" errors={errors} />
                        <FormTextField
                            label="Email"
                            control={control}
                            name="deliveryEmail"
                            errors={errors}
                            rules={{
                                min: 5,
                                pattern: {
                                    value: /^[^@]+@{1}[^\\.]+\..+/i,
                                    message: "This is not a valid email address."
                                }
                            }}
                        />
                        <FormTextField label="Subject" control={control} name="deliverySubject" errors={errors} />
                        <FormTextField
                            label="Message"
                            control={control}
                            name="deliveryMessage"
                            errors={errors}
                            multiline={true}
                        />
                        <FormActions
                            onCancel={handleCancel}
                            isLoading={formState.isSubmitting || formState.isValidating}
                        />
                    </Stack>
                </Stack>
            </Form>
        </>
    );
};

const getDeliveryTypes = (): DropdownOption[] => {
    return [{ key: DeliveryType.Upload.toString(), text: depascalize(DeliveryType[DeliveryType.Upload]) }];
};

export const mapFormInputToRequest = (
    deliveryEntityFormInputs: DeliveryInfoFormInputs,
    filePath: string,
    bucketName?: string
): CreateDeliveryRequest => ({
    deliveryName: deliveryEntityFormInputs.deliveryName,
    deliveryEmail: deliveryEntityFormInputs.deliveryEmail,
    deliverySubject: deliveryEntityFormInputs.deliverySubject,
    deliveryMessage: deliveryEntityFormInputs.deliveryMessage,
    arrivalDate: deliveryEntityFormInputs.arrivalDate,
    deliveryRef: deliveryEntityFormInputs.deliveryRef,
    deliveryTypeId: deliveryEntityFormInputs.deliveryType.toString(),
    originatorId: deliveryEntityFormInputs.originatorId,
    filePath,
    bucketName
});
